// ExportTab — Settings surface for downloading a `vaultwisp-backup-v1`
// file (Argon2id-derived ChaCha20-Poly1305-sealed `AppConfig`).
//
// One password field + confirm + zxcvbn ≥ 3 gate. The user picks any
// password — re-using the master is encouraged ("one less password to
// remember") but never auto-filled, since the master is never cached
// in app memory by design (CLAUDE.md).
//
// Download path mirrors `ExportDialog.tsx`: prefer
// `window.showSaveFilePicker` (single-shot, no in-memory buffering);
// fall back to an anchor + blob URL on browsers without it (Firefox).
// The Uint8Array of bytes is small (a few KiB at most) so the
// fallback's in-memory hop is acceptable.

import { useMemo, useState } from "react";
import { zxcvbn } from "@zxcvbn-ts/core";
import { useMasterSession } from "../../stores/masterSession";
import { useVault } from "../../hooks/useVault";
import { PassphraseInput } from "../primitives/PassphraseInput";
import { PassphraseStrength } from "../primitives/PassphraseStrength";
import { toPassphraseBytes, zeroize } from "../../lib/passphraseBytes";

type Phase = "idle" | "running" | "done" | "error";

const FILE_TYPE_DESCRIPTION = "vaultwisp backup";
const FILE_EXTENSION = ".vwb";
const FILE_MIME = "application/octet-stream";

function defaultFilename(now: Date = new Date()): string {
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `vaultwisp-backup-${yyyy}-${mm}-${dd}${FILE_EXTENSION}`;
}

function hasModernPicker(): boolean {
  // Optional chain handles SSR / Worker contexts where `globalThis.window`
  // is undefined; the strict `=== "function"` check then fails cleanly.
  // (Sonar S7741 flags `typeof X !== "undefined"`, so we sidestep it
  // by checking the picker property directly.)
  return typeof globalThis.window?.showSaveFilePicker === "function";
}

async function downloadViaPicker(bytes: Uint8Array, suggestedName: string): Promise<void> {
  const fileHandle = await globalThis.window.showSaveFilePicker!({
    suggestedName,
    types: [
      {
        description: FILE_TYPE_DESCRIPTION,
        accept: { [FILE_MIME]: [FILE_EXTENSION] },
      },
    ],
  });
  const writable = await fileHandle.createWritable();
  // Use a writer to match the streaming pattern in
  // `ExportDialog.tsx` (the augmented `FileSystemWritableFileStream`
  // shape extends `WritableStream<Uint8Array>` — the convenience
  // `.write()` overload is browser-only and not in our local
  // augmentation).
  let writeFailed = false;
  let writeError: unknown = null;
  const w = writable.getWriter();
  try {
    await w.write(bytes);
  } catch (err) {
    writeFailed = true;
    writeError = err;
  } finally {
    w.releaseLock();
  }
  if (writeFailed) {
    // On a partial-write failure the user otherwise lands with a
    // truncated, unverifiable file at the chosen path. Aborting the
    // writable cancels the pending data and lets the browser
    // surface the failure cleanly. Mirrors `ExportDialog.tsx`'s
    // abort wiring.
    await writable.abort(writeError).catch(() => {
      /* best-effort */
    });
    throw writeError;
  }
  await writable.close();
}

function downloadViaBlob(bytes: Uint8Array, suggestedName: string): void {
  // Copy into a fresh ArrayBuffer so the Blob isn't backed by a view
  // that could be mutated (or zeroized) before the browser finishes
  // the download.
  const buf = new Uint8Array(bytes.length);
  buf.set(bytes);
  const blob = new Blob([buf], { type: FILE_MIME });
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = suggestedName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Defer revoke so the browser has a chance to start the download.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

export function ExportTab() {
  const worker = useVault();
  const appConfig = useMasterSession((s) => s.master?.appConfig);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [phase, setPhase] = useState<Phase>("idle");
  const [error, setError] = useState<string | null>(null);

  const score = useMemo(
    () => (password.length > 0 ? zxcvbn(password).score : 0),
    [password],
  );
  const strong = score >= 3;
  const matches = password === confirm;
  const canSubmit =
    appConfig != null &&
    password.length > 0 &&
    confirm.length > 0 &&
    strong &&
    matches &&
    phase !== "running";

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!canSubmit || !appConfig) return;
    setPhase("running");
    setError(null);
    const passwordBytes = toPassphraseBytes(password);
    let bytes: Uint8Array | null = null;
    try {
      bytes = await worker.sealBackup(appConfig, passwordBytes);
      const suggested = defaultFilename();
      if (hasModernPicker()) {
        try {
          await downloadViaPicker(bytes, suggested);
        } catch (err) {
          // User cancelling the picker throws AbortError — keep the
          // form in `idle` so they can retry without the success
          // confirmation flashing.
          if (err instanceof DOMException && err.name === "AbortError") {
            setPhase("idle");
            return;
          }
          throw err;
        }
      } else {
        downloadViaBlob(bytes, suggested);
      }
      setPhase("done");
      // Wipe the local form state once the download is dispatched so
      // the password doesn't sit in React state across the session.
      setPassword("");
      setConfirm("");
    } catch (err) {
      setPhase("error");
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      zeroize(passwordBytes);
      // Best-effort: blank any in-memory copy of the sealed file
      // ASAP. The browser may still be reading it via the picker
      // stream (already pumped above) or via the Blob URL (already
      // dispatched), but anything beyond that hop is out of scope.
      if (bytes) {
        bytes.fill(0);
      }
    }
  }

  return (
    <div className="max-w-xl space-y-6">
      <h2 className="text-xl font-medium">Export backup</h2>
      <p className="max-w-[60ch] text-xs text-text-soft">
        Download an encrypted file containing your vault list, adapter
        configurations, and synced vault credentials.{" "}
        <strong className="text-warn">
          OPFS vault contents (files you've uploaded to your primary
          vault) are NOT included.
        </strong>{" "}
        Carry this file to a new device and import it from the welcome
        screen to restore your settings.
      </p>
      <p className="max-w-[60ch] text-[11px] text-text-soft">
        Pick any password to encrypt the file. Re-using your master
        password is fine and means one less password to remember; a
        different password means a stolen master alone can't decrypt
        this file.
      </p>

      <form className="space-y-4" onSubmit={(e) => void onSubmit(e)}>
        <div>
          <label
            htmlFor="export-password"
            className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
          >
            Backup password
          </label>
          <PassphraseInput
            id="export-password"
            autoCompleteMode="new-password"
            aria-label="Backup password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <PassphraseStrength passphrase={password} />
          {!strong && password.length > 0 && (
            <div className="mt-1.5 text-[12px] text-danger">
              Too weak — use 5+ random words.
            </div>
          )}
        </div>

        <div>
          <label
            htmlFor="export-confirm"
            className="mb-1 block text-[11px] uppercase tracking-[0.1em] text-text-dim"
          >
            Confirm backup password
          </label>
          <PassphraseInput
            id="export-confirm"
            autoCompleteMode="new-password"
            aria-label="Confirm backup password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {!matches && confirm.length > 0 && (
            <div className="mt-1.5 text-[12px] text-danger">
              Confirmation does not match.
            </div>
          )}
        </div>

        {error !== null && (
          <div
            role="alert"
            className="rounded-md border border-danger/40 bg-danger/10 p-3 text-[13px] text-danger"
          >
            <span className="font-mono">Export failed: {error}</span>
          </div>
        )}

        {phase === "done" && (
          <output
            aria-live="polite"
            aria-atomic="true"
            className="block rounded-md border border-ok/40 bg-ok/10 p-3 text-[13px] text-ok"
          >
            Backup downloaded. Store it somewhere only you can reach.
          </output>
        )}

        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
        >
          {phase === "running" ? "Encrypting…" : "Download backup"}
        </button>
      </form>
    </div>
  );
}
